import {Component, effect, inject, OnDestroy, OnInit, Signal} from '@angular/core';
import {Store} from '@ngrx/store';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {IUserAccessUserTableSettings} from '../../../user-access/models/user-access-features/access';
import {SettingsReferenceEnum} from '../../models/general/settings-reference.enum';
import {
  selectApplicationUserTableSettings,
  selectNgpReportEditsPreview,
  selectSelectedSetting,
  selectStockManagerEditsPreview,
} from '../../store/setting.selectors';
import {IUser} from '../../../user-access/models';
import {selectUser} from '../../../user-access/user-store/user.selectors';
import {selectUserAccessUserTableSettings} from '../../../user-access/user-access-store/user-access.selectors';
import {DEFAULT_APPLICATION_TEXT, _DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {ITableNavSettings} from '../../models';
import {
  settingsSetSelected,
  settingUserEditPreviewColsNGPReport,
  settingUserEditPreviewColsStock,
  settingUserTablePreferences,
} from '../../store/setting.actions';
import {ISharedModalBasic} from 'src/app/shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from 'src/app/shared/shared-services/overlay/modal-controller.service';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {TTableActions} from '../../models';
import {IHasUnsavedChanges, UnsavedChangesService} from '../../services/unsaved-changes/unsaved-changes.service';
import {KEY_FOR_STORAGE_MAP, USER_SETTINGS} from '../../models/user-table-settings/user-settings.constants';

@Component({
  selector: 'app-user-table-settings',
  templateUrl: './user-table-settings.component.html',
  styleUrl: './user-table-settings.component.scss',
  standalone: false,
})
export class UserTableSettingsComponent implements OnInit, OnDestroy, IHasUnsavedChanges {

  //#region INJECTORS
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  private unsavedChangesService = inject(UnsavedChangesService);
  //#endregion

  //#region PROPERTIES
  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly text = DEFAULT_APPLICATION_TEXT;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;
  protected readonly ngpPreviewColumnsConstants = USER_SETTINGS.tableSettings.ngpEditsPreview;
  protected readonly stockManagerPreviewColumnsConstants =
    USER_SETTINGS.tableSettings.stockEditsPreview;

  #selectedEnterNavigation: string[] = [];
  #selectedTabNavigation: string[] = [];

  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _tableSettings: Signal<ITableNavSettings> = this._store.selectSignal(selectApplicationUserTableSettings);
  private readonly _user: Signal<IUser> = this._store.selectSignal(selectUser);
  private readonly _uaUserTableSettings: Signal<IUserAccessUserTableSettings> = this._store.selectSignal(selectUserAccessUserTableSettings);
  private readonly _ngpEditsPreview: Signal<{ [index: number]: string }> = this._store.selectSignal(selectNgpReportEditsPreview);
  private readonly _stockEditsPreview: Signal<{ [index: number]: string }> = this._store.selectSignal(selectStockManagerEditsPreview);
  #hasEnterNavigationBeenEdited: boolean = false;
  #hasTabNavigationBeenEdited: boolean = false;
  #hasNgpPreviewBeenEdited: boolean = false;
  #hasStockPreviewBeenEdited: boolean = false;

  protected ngpPreviewSelectedColumns: Record<string, ISelectOption>;
  protected stockPreviewSelectedColumns: Record<string, ISelectOption>;
  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const settings = this._tableSettings();
      this.selectedEnterNav = [this.getKeyForStorage(settings?.enterEnd ?? 'restart_page')];
      this.selectedTabNav = [this.getKeyForStorage(settings?.tabEnd ?? 'restart_page')];
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });
    effect(() => {
      const preview = this._ngpEditsPreview();
      if (preview) {
        this.ngpPreviewSelectedColumns = this.visibleFieldsToOptions(preview, this.ngpPreviewColumnsConstants.options);
      }
    });
    effect(() => {
      const preview = this._stockEditsPreview();
      if (preview) {
        this.stockPreviewSelectedColumns = this.visibleFieldsToOptions(preview, this.stockManagerPreviewColumnsConstants.options);
      }
    });

    // Register with unsaved changes service
    this.unsavedChangesService.registerComponent(this);
  }

  //#endregion

  //#region ACCESSORS - SELECTED
  get selectedEnterNav(): string[] {
    return this.#selectedEnterNavigation;
  }

  get selectedTabNav(): string[] {
    return this.#selectedTabNavigation;
  }

  //#endregion

  //#region ACCESSOR - ENTER NAVIGATION

  get hasEnterNavigationDetailsBeenEdited(): boolean {
    return this.#hasEnterNavigationBeenEdited;
  }

  get isEnterNavigationDetailsSubmitDisabled(): boolean {
    return (this.canEnterNavigationDetailsBeEdited === false) || (this.canEnterNavigationDetailsBeEdited === true && this.hasEnterNavigationDetailsBeenEdited === false);
  }

  get canEnterNavigationDetailsBeEdited(): boolean {
    const access = this.tableSettings?.enterNavigationMode;
    return access?.canEdit ?? false;
  }

  get canEnterNavigationDetailsBeSubmitted(): boolean {
    const access = this.tableSettings?.enterNavigationMode;
    return access?.canSubmit ?? false;
  }

  get canEnterNavigationDetailsBeViewed(): boolean {
    const access = this.tableSettings?.enterNavigationMode;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - TAB NAVIGATION

  get hasTabNavigationDetailsBeenEdited(): boolean {
    return this.#hasTabNavigationBeenEdited;
  }

  get isTabNavigationDetailsSubmitDisabled(): boolean {
    return (this.canTabNavigationDetailsBeEdited === false) || (this.canTabNavigationDetailsBeEdited === true && this.hasTabNavigationDetailsBeenEdited === false);
  }

  get canTabNavigationDetailsBeEdited(): boolean {
    const access = this.tableSettings?.tabNavigationMode;
    return access?.canEdit ?? false;
  }

  get canTabNavigationDetailsBeSubmitted(): boolean {
    const access = this.tableSettings?.tabNavigationMode;
    return access?.canSubmit ?? false;
  }

  get canTabNavigationDetailsBeViewed(): boolean {
    const access = this.tableSettings?.tabNavigationMode;
    return access?.canView ?? false;
  }

  //#endregion
  //#region ACCESSOR - NGP Columns

  get hasNGPColumnsDetailsBeenEdited(): boolean {
    return this.#hasNgpPreviewBeenEdited;
  }

  get isNGPColumnsDetailsSubmitDisabled(): boolean {
    return (this.canNGPColumnsDetailsBeEdited === false) || (this.canNGPColumnsDetailsBeEdited === true && this.hasNGPColumnsDetailsBeenEdited === false);
  }

  get canNGPColumnsDetailsBeEdited(): boolean {
    const access = this.tableSettings?.ngpPreview;
    return access?.canEdit ?? false;
  }

  get canNGPColumnsDetailsBeSubmitted(): boolean {
    const access = this.tableSettings?.ngpPreview;
    return access?.canSubmit ?? false;
  }

  get canNGPColumnsDetailsBeViewed(): boolean {
    const access = this.tableSettings?.ngpPreview;
    return access?.canView ?? false;
  }

  //#region ACCESSOR - STOCK Columns

  get hasStockColumnsDetailsBeenEdited(): boolean {
    return this.#hasStockPreviewBeenEdited;
  }

  get isStockColumnsDetailsSubmitDisabled(): boolean {
    return (this.canStockColumnsDetailsBeEdited === false) || (this.canStockColumnsDetailsBeEdited === true && this.hasStockColumnsDetailsBeenEdited === false);
  }

  get canStockColumnsDetailsBeEdited(): boolean {
    const access = this.tableSettings?.stockPreview;
    return access?.canEdit ?? false;
  }

  get canStockColumnsDetailsBeSubmitted(): boolean {
    const access = this.tableSettings?.stockPreview;
    return access?.canSubmit ?? false;
  }

  get canStockColumnsDetailsBeViewed(): boolean {
    const access = this.tableSettings?.stockPreview;
    return access?.canView ?? false;
  }

  //#endregion

  protected get ngpPreviewOptions(): Record<string, ISelectOption> {
    const base = this.ngpPreviewColumnsConstants?.options;
    if (!base) return {};
    if (this.ngpPreviewSelectedColumns) {
      const merged: Record<string, ISelectOption> = {};
      Object.entries(base).forEach(([key, option]) => {
        merged[key] = {
          ...option,
          selected: this.ngpPreviewSelectedColumns[key]?.selected ?? option.selected,
        };
      });
      return this.buildSortedOptions(merged);
    }
    return this.buildSortedOptions(base);
  }

  protected get ngpPreviewSelectedKeys(): string[] {
    return Object.keys(this.ngpPreviewOptions)
      .filter(key => this.ngpPreviewOptions[key].selected);
  }



  protected get stockManagerPreviewOptions(): Record<string, ISelectOption> {
    const base = this.stockManagerPreviewColumnsConstants?.options;
    if (!base) return {};
    if (this.stockPreviewSelectedColumns) {
      const merged: Record<string, ISelectOption> = {};
      Object.entries(base).forEach(([key, option]) => {
        merged[key] = {
          ...option,
          selected: this.stockPreviewSelectedColumns[key]?.selected ?? option.selected,
        };
      });
      return this.buildSortedOptions(merged);
    }
    return this.buildSortedOptions(base);
  }

  protected get stockManagerPreviewSelectedKeys(): string[] {
    return Object.keys(this.stockManagerPreviewOptions)
      .filter(key => this.stockManagerPreviewOptions[key].selected);
  }
  //#endregion

  //#region ACCESSOR - TABLE SETTINGS
  get tableSettings(): IUserAccessUserTableSettings {
    return this._uaUserTableSettings();
  }

  //#endregion

  //#region ACCESSOR - USER
  get userSettings(): IUser {
    return this._user();
  }

  //#endregion

  //#region ACCESSOR - NGP PREVIEW

  get hasNgpPreviewBeenEdited(): boolean {
    return this.#hasNgpPreviewBeenEdited;
  }

  get isNgpPreviewSubmitDisabled(): boolean {
    return !this.#hasNgpPreviewBeenEdited;
  }

  //#endregion

  //#region ACCESSOR - STOCK PREVIEW

  get hasStockPreviewBeenEdited(): boolean {
    return this.#hasStockPreviewBeenEdited;
  }

  get isStockPreviewSubmitDisabled(): boolean {
    return !this.#hasStockPreviewBeenEdited;
  }

  //#endregion

  //#region ACCESSOR - ALL/GLOBAL
  get haveAnyEditsBeenMade(): boolean {
    return this.#hasEnterNavigationBeenEdited || this.#hasTabNavigationBeenEdited
      || this.#hasNgpPreviewBeenEdited || this.#hasStockPreviewBeenEdited;
  }

  //#endregion

  //#region SETTERS - ALL
  set selectedEnterNav(value: string[]) {
    this.#selectedEnterNavigation = value;
  }

  set selectedTabNav(value: string[]) {
    this.#selectedTabNavigation = value;
  }

  set hasEnterNavigationDetailsBeenEdited(value: boolean) {
    this.#hasEnterNavigationBeenEdited = value;
  }

  set hasTabNavigationDetailsBeenEdited(value: boolean) {
    this.#hasTabNavigationBeenEdited = value;
  }

  set hasNgpColumnsDetailsBeenEdited(value: boolean) {
    this.#hasNgpPreviewBeenEdited = value;
  }

  set hasStockManagerDetailsBeenEdited(value: boolean) {
    this.#hasStockPreviewBeenEdited = value;
  }
  //#endregion

  //#region LIFECYCLE

  ngOnInit(): void {
    this._store.dispatch(settingUserTablePreferences.getTablePreferences({}));
    this._store.dispatch(settingUserEditPreviewColsNGPReport.getEditPreviewColsNGPReport({}));
    this._store.dispatch(settingUserEditPreviewColsStock.getEditPreviewColsStock({}));
  }

  ngOnDestroy(): void {
    this.unsavedChangesService.unregisterComponent();
  }

  //#endregion

  //#region FORM - CLICK HANDLERS
  onRevertForm(form: string): void {
    const tableSettings = this._tableSettings();
    switch (form) {
      case 'all':
        this.hasEnterNavigationDetailsBeenEdited = false;
        this.hasTabNavigationDetailsBeenEdited = false;
        this.selectedEnterNav = [this.getKeyForStorage(tableSettings.enterEnd)];
        this.selectedTabNav = [this.getKeyForStorage(tableSettings.tabEnd)];
        this.#hasNgpPreviewBeenEdited = false;
        this.#hasStockPreviewBeenEdited = false;
        if (this._ngpEditsPreview()) {
          this.ngpPreviewSelectedColumns = this.visibleFieldsToOptions(this._ngpEditsPreview(), this.ngpPreviewColumnsConstants.options);
        }
        if (this._stockEditsPreview()) {
          this.stockPreviewSelectedColumns = this.visibleFieldsToOptions(this._stockEditsPreview(), this.stockManagerPreviewColumnsConstants.options);
        }
        break;
      case 'enter':
        this.hasEnterNavigationDetailsBeenEdited = false;
        this.selectedEnterNav = [this.getKeyForStorage(tableSettings.enterEnd)];
        break;
      case 'tab':
        this.hasTabNavigationDetailsBeenEdited = false;
        this.selectedTabNav = [this.getKeyForStorage(tableSettings.tabEnd)];
        break;
      case 'ngp':
        this.hasNgpColumnsDetailsBeenEdited = false;
        if (this._ngpEditsPreview()) {
          this.ngpPreviewSelectedColumns = this.visibleFieldsToOptions(this._ngpEditsPreview(), this.ngpPreviewColumnsConstants.options);
        }
        break;
      case 'stock':
        this.hasStockManagerDetailsBeenEdited = false;
        if (this._stockEditsPreview()) {
          this.stockPreviewSelectedColumns = this.visibleFieldsToOptions(this._stockEditsPreview(), this.stockManagerPreviewColumnsConstants.options);
        }
        break;
    }
  }

  onSubmitForm(form: string): void {
    const settings: ITableNavSettings = {...this._tableSettings()};
    switch (form) {
      case 'all':
        this.hasEnterNavigationDetailsBeenEdited = false;
        this.hasTabNavigationDetailsBeenEdited = false;
        settings.enterEnd = <TTableActions>this.getKeyForStorage(this.selectedEnterNav[0] ?? 'Jump back to first field on current page');
        settings.tabEnd = <TTableActions>this.getKeyForStorage(this.selectedTabNav[0] ?? 'Jump back to first field on current page');
        this._store.dispatch(settingUserTablePreferences.setTablePreferences({settings}));
        if (this.ngpPreviewSelectedColumns) {
          this.#hasNgpPreviewBeenEdited = false;
          this._store.dispatch(settingUserEditPreviewColsNGPReport.setEditPreviewColsNGPReport({
            visibleFields: this.optionsToVisibleFields(this.ngpPreviewSelectedColumns),
          }));
        }
        if (this.stockPreviewSelectedColumns) {
          this.#hasStockPreviewBeenEdited = false;
          this._store.dispatch(settingUserEditPreviewColsStock.setEditPreviewColsStock({
            visibleFields: this.optionsToVisibleFields(this.stockPreviewSelectedColumns),
          }));
        }
        return;
      case 'enter':
        this.hasEnterNavigationDetailsBeenEdited = false;
        settings.enterEnd = <TTableActions>this.getKeyForStorage(this.selectedEnterNav[0] ?? 'Jump back to first field on current page');
        break;
      case 'tab':
        this.hasTabNavigationDetailsBeenEdited = false;
        settings.tabEnd = <TTableActions>this.getKeyForStorage(this.selectedTabNav[0] ?? 'Jump back to first field on current page');
        break;
      case 'ngp':
        if (this.ngpPreviewSelectedColumns) {
          this.#hasNgpPreviewBeenEdited = false;
          this._store.dispatch(settingUserEditPreviewColsNGPReport.setEditPreviewColsNGPReport({
            visibleFields: this.optionsToVisibleFields(this.ngpPreviewSelectedColumns),
          }));
        }
        return;
      case 'stock':
        if (this.stockPreviewSelectedColumns) {
          this.#hasStockPreviewBeenEdited = false;
          this._store.dispatch(settingUserEditPreviewColsStock.setEditPreviewColsStock({
            visibleFields: this.optionsToVisibleFields(this.stockPreviewSelectedColumns),
          }));
        }
        return;
    }
    this._store.dispatch(settingUserTablePreferences.setTablePreferences({settings}));
  }

  onChangeEnterNavigationOption(selected: { [key: string]: ISelectOption }): void {
    const selection = Object.keys(selected)
      .filter((storeId: string) => selected[storeId].selected);
    this.selectedEnterNav = selection?.length > 0 ? selection : [];
    const previous = this.getKeyForStorage(selection[0] ?? 'Jump back to first field on current page');
    if (previous !== this._tableSettings().enterEnd) {
      this.#hasEnterNavigationBeenEdited = true;
    }
  }

  onChangeTabNavigationOption(selected: { [key: string]: ISelectOption }): void {
    const selection = Object.keys(selected)
      .filter((storeId: string) => selected[storeId].selected);
    this.selectedTabNav = selection?.length > 0 ? selection : [];
    const previous = this.getKeyForStorage(selection[0] ?? 'Jump back to first field on current page');

    if (previous !== this._tableSettings().tabEnd) {
      this.#hasTabNavigationBeenEdited = true;
    }
  }

  navigationOptions(form: 'enter' | 'tab'): { [key: string]: ISelectOption } {
    const options: { [key: string]: ISelectOption } = {};
    const baseOptions = form === 'enter'
      ? ['restart_page','restart_column','next_page','next_column','next_page_same_column']
      : ['restart_page','restart_column','next_page','restart_row'];

    baseOptions.forEach((key) => {
      options[key] = {
        name: KEY_FOR_STORAGE_MAP[key],
        selected: false,
        disabled: false,
      };
    });

    return options;
  }

  protected onNgpPreviewChange(selected: Record<string, ISelectOption>): void {
    const baseOptions = this.ngpPreviewColumnsConstants?.options;
    if (!baseOptions) return;

    const updated: Record<string, ISelectOption> = {};

    Object.entries(baseOptions).forEach(([key, original]) => {
      updated[key] = {
        ...original,
        selected: !!selected[key]?.selected,
      };
    });

    this.ngpPreviewSelectedColumns = updated;
    this.#hasNgpPreviewBeenEdited = true;
  }

  protected onStockManagerPreviewChange(selected: Record<string, ISelectOption>): void {
    const baseOptions = this.stockManagerPreviewColumnsConstants?.options;
    if (!baseOptions) return;

    const updated: Record<string, ISelectOption> = {};

    Object.entries(baseOptions).forEach(([key, original]) => {
      updated[key] = {
        ...original,
        selected: !!selected[key]?.selected,
      };
    });

    this.stockPreviewSelectedColumns = updated;
    this.#hasStockPreviewBeenEdited = true;
  }

  //#endregion

  //#region GENERATORS

  private getKeyForStorage(key: string): string {
    return Object.entries(KEY_FOR_STORAGE_MAP).find(([k, v]) => v === key)?.[0] || key;
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  /**
   * Shows a modal asking the user if they want to discard unsaved changes
   * @returns Promise that resolves to true if user wants to proceed (discard changes), false if user cancels
   */
  async onShowModalUnsavedChanges(): Promise<boolean> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };

    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    return result.role === 'modal-accept';
  }

  //#endregion

  /**
   * Converts a stored visibleFields map (numeric index → field key) to a full options map with correct selected states
   */
  private visibleFieldsToOptions(
    visibleFields: { [index: number]: string },
    baseOptions: Record<string, ISelectOption>,
  ): Record<string, ISelectOption> {
    const selectedKeys = new Set(Object.values(visibleFields));
    const result: Record<string, ISelectOption> = {};
    Object.entries(baseOptions).forEach(([key, option]) => {
      result[key] = {
        ...option,
        selected: option.disabled ? option.selected : selectedKeys.has(key),
      };
    });
    return result;
  }

  /**
   * Converts a full options map to the stored visibleFields format (numeric index → selected field key)
   */
  private optionsToVisibleFields(options: Record<string, ISelectOption>): { [index: number]: string } {
    const visibleFields: { [index: number]: string } = {};
    let index = 0;
    Object.entries(options)
      .filter(([, option]) => option.selected)
      .forEach(([key]) => {
        visibleFields[index++] = key;
      });
    return visibleFields;
  }

  private buildSortedOptions(optionsMap?: Record<string, ISelectOption>): Record<string, ISelectOption> {
    if (!optionsMap) return {};

    return Object.fromEntries(
      Object.entries(optionsMap)
        .sort(([, a], [, b]) => {
          if (a.selected !== b.selected) {
            return a.selected ? -1 : 1;
          }

          if (a.disabled !== b.disabled) {
            return a.disabled ? 1 : -1;
          }

          return a.name.localeCompare(b.name);
        })
        .map(([key, value]) => [key, { ...value }])
    );
  }

  //#endregion

}
